'use client';

import {
  BreadcrumbItem,
  BreadcrumbLink,
  Breadcrumb as DsBreadcrumb,
} from '@ironhack/design-system2/components/breadcrumb';
import { type FlexProps } from '@ironhack/design-system2/components/flex';
import { Icon } from '@ironhack/design-system2/components/icon';
import { ChevronRight } from '@ironhack/design-system2/icons/chevron-right';
import NextLink from 'next/link';
import {
  converge,
  drop,
  head,
  join,
  pair,
  pipe,
  range,
  reject,
  split,
  take,
  takeWhile,
} from 'ramda';
import { type ReactElement } from 'react';
import {
  type BreadcrumbList,
  type ListItem,
  type WithContext,
} from 'schema-dts';

import { ModuleWrapper } from '@/app/components/client/module-wrapper';
import { SeoStructuredDataScript } from '@/app/components/server/seo-structured-data-script';
import {
  type BasePageParams,
  type PageType,
  getPageUrl,
  getUrlRegion,
} from '@/app/urls/url-mapping';
import config from '@/config';
import { toTitleCase } from '@/lib/utils';

type Props = {
  page: PageType;
  params: Pick<BasePageParams, 'language' | 'region'>;
  omitSlugs?: string[];
} & FlexProps;

export const Breadcrumb = (props: Props): ReactElement | null => {
  const { page, params, omitSlugs = [], ...flexProps } = props;

  // usePathname returns internal URLs sometimes, so generate the URL this way.
  const pathname = getPageUrl(page, params);
  if (!pathname) return null;

  const ignoreExtraParamsPages = takeWhile(
    (part: string) => !['page', 'c', 'e'].includes(part)
  );

  const shouldOmit = (part: string) => omitSlugs.includes(part);
  const omitPathSlugs = (parts: string[]) => reject(shouldOmit, parts);

  const [basePath, pathParts] = pipe(
    split('?'),
    head,
    split('/'),
    // @ts-expect-error -- converge typing remains a mystery
    converge(pair, [
      pipe(take(2), join('/')),
      pipe(drop(2), omitPathSlugs, ignoreExtraParamsPages),
    ]) as (s: string[]) => [string, string[]]
  )(pathname);

  const urlRegion = getUrlRegion(params);
  const breadcrumbs = [
    {
      route: basePath,
      title: `Ironhack ${urlRegion.toUpperCase()}`,
    },
    ...range(0, pathParts.length).map((index) => ({
      route: `${basePath}/${take(index + 1, pathParts).join('/')}`,
      title: toTitleCase(pathParts[index]).replace('#', ''),
    })),
  ];

  // Consider using <script> if this BreadcrumbList is impactful to SEO
  const seoStructuredData: WithContext<BreadcrumbList> = {
    '@context': 'https://schema.org',
    '@id': `${config.baseUrl}${pathname}#breadcrumb-list`,
    '@type': 'BreadcrumbList',
    itemListElement: breadcrumbs.map(
      (breadcrumb, index): ListItem => ({
        '@type': 'ListItem',
        item: {
          '@id': `${config.baseUrl}${breadcrumb.route}`,
          name: breadcrumb.title,
        },
        position: index + 1,
      })
    ),
  };

  return (
    <ModuleWrapper
      moduleFormat={{
        background: 'transparent',
        spacingTop: '16',
        spacingBottom: '16',
      }}
      outerProps={{
        bg: 'fill.preFooter',
        ...flexProps,
      }}
    >
      <DsBreadcrumb
        // Breadcrumb needs to be a client component to pass this prop
        separator={
          <Icon as={ChevronRight} boxSize={6} color="text.inverse" mt={0.5} />
        }
      >
        {breadcrumbs.map(({ route, title }, index) => (
          <BreadcrumbItem
            color="text.inverse"
            isCurrentPage={index === breadcrumbs.length - 1}
            key={route}
            textStyle="m"
          >
            {index === breadcrumbs.length - 1 ? (
              <span aria-current="page">{title}</span>
            ) : (
              <BreadcrumbLink as={NextLink} href={route} passHref>
                {title}
              </BreadcrumbLink>
            )}
          </BreadcrumbItem>
        ))}
      </DsBreadcrumb>
      <SeoStructuredDataScript
        id="structured-data-breadcrumb"
        structuredData={seoStructuredData}
      />
    </ModuleWrapper>
  );
};
